/**
 * COMMERCE-LAUNCH-168D6 — single checkout readiness evaluator.
 * Returns ready + ordered human blockers for disabled CTA explanation.
 */
import {
  validateCheckout,
  type CheckoutFieldErrors,
  type CheckoutInputForValidation,
} from "./checkout-validation-v2.js";

export type CheckoutReadiness = {
  ready: boolean;
  blockers: string[];
  errors: CheckoutFieldErrors;
};

export type CheckoutDeliveryGate =
  | {
      npRefEnabled: true;
      npManualFallback: boolean;
      settlementRef: string | null | undefined;
      warehouseRef: string | null | undefined;
      mode: "warehouse" | "address" | string | null | undefined;
      city: string;
      warehouse: string;
    }
  | {
      npRefEnabled: false;
      city: string;
      warehouse: string;
    };

export function evaluateCheckoutReadiness(
  input: CheckoutInputForValidation & {
    itemCount: number;
    submitting?: boolean;
    preparingHandoff?: boolean;
    delivery: CheckoutDeliveryGate;
  },
): CheckoutReadiness {
  const blockers: string[] = [];
  if (input.submitting || input.preparingHandoff) {
    return { ready: false, blockers: ["Зачекайте…"], errors: {} };
  }
  if (input.itemCount <= 0) {
    blockers.push("Додайте товари до кошика");
  }
  const validation = validateCheckout(input);
  const err = validation.errors;
  if (err.name || err.surname) blockers.push("Вкажіть ім’я та прізвище");
  if (err.phone) blockers.push(err.phone === "Вкажіть номер телефону." ? "Вкажіть телефон" : err.phone);
  if (err.paymentMethod) blockers.push("Оберіть спосіб оплати");
  if (err.note) blockers.push(err.note);

  const d = input.delivery;
  if (d.npRefEnabled && !d.npManualFallback) {
    if (!d.settlementRef) blockers.push("Оберіть населений пункт");
    else if (d.mode !== "address" && !d.warehouseRef) blockers.push("Оберіть відділення");
  } else {
    if (!String(d.city ?? "").trim()) blockers.push("Оберіть спосіб доставки / місто");
    else if (!String(d.warehouse ?? "").trim()) blockers.push("Оберіть відділення або адресу");
  }
  if (err.city && !blockers.some((b) => b.includes("доставки") || b.includes("пункт"))) {
    blockers.push(err.city);
  }
  if (err.warehouse && !blockers.some((b) => b.includes("відділення"))) {
    blockers.push(err.warehouse);
  }

  // Dedup while preserving order
  const seen = new Set<string>();
  const unique = blockers.filter((b) => {
    if (seen.has(b)) return false;
    seen.add(b);
    return true;
  });

  return {
    ready: unique.length === 0 && validation.ok,
    blockers: unique,
    errors: err,
  };
}
